import type { ChatMessage } from '../types/chat'
import type { PatientContext } from '../types/widget'
import { isFrenchLanguage } from '../utils/chatUiLocale'

const uid = () => Math.random().toString(16).slice(2) + '-' + Date.now().toString(16)

/** Doctor-facing opener — ignores embed ``patientContext`` (Laravel may send demo/placeholder names). */
export function copilotInitialMessages(): ChatMessage[] {
  const fr = isFrenchLanguage()
  const greeting = fr ? 'Bonjour ! Comment puis-je vous aider ?' : 'Hello! How can I help you?'

  return [
    {
      id: uid(),
      role: 'assistant',
      content: greeting,
      timestamp: Date.now(),
    },
  ]
}

/** @deprecated Use ``copilotInitialMessages``; embed patient name is intentionally not used. */
export const mockInitialMessages = (_patientContext?: PatientContext): ChatMessage[] =>
  copilotInitialMessages()

/** Mirrors backend conversational_turns loosely — used when /chat/command returns no rows. */
function mockConversationalReply(text: string): string | null {
  const t = text.trim().toLowerCase().replace(/[!?.…,;:]+$/, '').trim()
  if (!t || t.length > 160) return null
  if (/\b(patient|dossier|go\s+to|open\s+the|prescription|agenda)\b/i.test(text)) return null
  if (/^(hi|hello|hey|yo|bonjour|salut|coucou|good\s+(morning|afternoon|evening))(\s+there)?$/i.test(t)) {
    return 'Hello! How can I help you today?'
  }
  if (/^(thanks?|thank\s+you|thx|merci)(\s+(very\s+much|a\s+lot|so\s+much))?$/i.test(t)) {
    return "You're welcome. Let me know if you need anything else."
  }
  if (/^(ok(ay)?|yes|yeah|got\s+it|understood|sure)$/i.test(t)) {
    return "Sounds good. I'm here whenever you need me."
  }
  if (/^how\s+are\s+you(\s+today)?\??$/i.test(t) || /^what'?s\s+up\??$/i.test(t)) {
    return "I'm doing well, thanks! What would you like to do?"
  }
  return null
}

export const mockReplyFor = async (args: {
  text: string
  clientId?: string
  tenantId?: string
  apiBaseUrl?: string
  patientContext?: PatientContext
  history: ChatMessage[]
}): Promise<string> => {
  const conv = mockConversationalReply(args.text)
  if (conv) {
    await new Promise((r) => setTimeout(r, 400))
    return conv
  }

  const t = args.text.trim().toLowerCase()

  // Very lightweight mocked "intent" so the UI feels real.
  const hint = (() => {
    if (t.includes('appointment') || t.includes('schedule')) return 'I can help you check or schedule an appointment.'
    if (t.includes('pain')) return 'I can help you note your pain level and timing for the care team.'
    if (t.includes('medication') || t.includes('meds')) return 'I can help you review medication questions and reminders.'
    if (t.includes('symptom') || t.includes('fever') || t.includes('cough')) return 'I can help you track symptoms and when they started.'
    return 'Thanks. I’m logging that and will prepare a summary for the care team.'
  })()

  const patientBit = args.patientContext?.patientId ? ` (Patient ID: ${args.patientContext.patientId})` : ''
  const clientBit = args.tenantId ? ` for tenant ${args.tenantId}` : ''

  const reply = `${hint}${patientBit}${clientBit} For now, this is mocked—no backend is connected yet.`

  await new Promise((r) => setTimeout(r, 600))
  return reply
}

